import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  listUsers,
  selectUsers,
  selectUsersError,
  selectUsersLoading,
  selectUsersMessage,
} from "../userSlice";

const PAGE_SIZE = 10;

const UsersListPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const users = useSelector(selectUsers);
  const isLoading = useSelector(selectUsersLoading);
  const isError = useSelector(selectUsersError);
  const message = useSelector(selectUsersMessage);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [packageFilter, setPackageFilter] = useState("all");

  useEffect(() => {
    dispatch(listUsers());
  }, [dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const packageType = (params.get("package") || "").toLowerCase();
    if (packageType === "premium" || packageType === "standard") {
      setPackageFilter(packageType);
    } else {
      setPackageFilter("all");
    }
    setPage(1);
  }, [location.search]);

  const getFullName = (user) => {
    if (user?.fullName) return user.fullName;
    const nameParts = [user?.name, user?.surName].filter(Boolean);
    return nameParts.length ? nameParts.join(" ") : "";
  };

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const safeText = text || "";
    const lower = safeText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lower.indexOf(lowerTerm);
    if (index === -1) return safeText || "-";
    const before = safeText.slice(0, index);
    const match = safeText.slice(index, index + term.length);
    const after = safeText.slice(index + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "yellow" }}>
          {match}
        </span>
        {after}
      </>
    );
  };

  const getEmail = (user) => {
    return user?.email || user?.userEmail || "-";
  };

  const getRole = (user) => {
    return user?.role || user?.userType || "-";
  };

  const isPremiumUser = (user) => Boolean(user?.premium);

  const formatDate = (value) => {
    if (!value) return "-";
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleString("tr-TR");
    }
    if (typeof value === "number") {
      return new Date(value).toLocaleString("tr-TR");
    }
    if (typeof value === "string") return value;
    return "-";
  };

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    const searched = !term ? (users || []) : (users || []).filter((user) => {
      const name = getFullName(user).toLowerCase();
      return name.includes(term);
    });
    if (packageFilter === "premium") {
      return searched.filter((user) => isPremiumUser(user));
    }
    if (packageFilter === "standard") {
      return searched.filter((user) => !isPremiumUser(user));
    }
    return searched;
  }, [users, search, packageFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const renderRows = () => {
    if (isLoading && (!users || users.length === 0)) {
      return (
        <tr>
          <td colSpan="9">{t("admin_common.loading")}</td>
        </tr>
      );
    }
    if (isError && (!users || users.length === 0)) {
      return (
        <tr>
          <td colSpan="9" className="text-danger">
            {message || t("admin_common.error_generic")}
          </td>
        </tr>
      );
    }
    if (!pagedUsers || pagedUsers.length === 0) {
      return (
        <tr>
          <td colSpan="9">{t("admin_common.no_records")}</td>
        </tr>
      );
    }
    return pagedUsers.map((user) => {
      const fullName = getFullName(user);
      const isPremium = isPremiumUser(user);
      return (
      <tr key={user.id || user.userId}>
        <td>{user?.userId || user?.id || "-"}</td>
        <td>
          {isPremium && <i className="fa fa-star text-warning me-2" aria-hidden="true" />}
          {highlightMatch(fullName || "-", search.trim())}
        </td>
        <td>{getEmail(user)}</td>
        <td>{getRole(user)}</td>
        <td>{user?.isActive ? t("admin_common.status_active") : t("admin_common.status_passive")}</td>
        <td>
          <span className={`badge ${isPremium ? "badge-warning" : "badge-secondary"}`}>
            {isPremium ? t("admin_common.premium") : t("admin_common.standard")}
          </span>
        </td>
        <td>{formatDate(user?.pricingDate)}</td>
        <td>{formatDate(user?.demostartdate || user?.demoStartDate)}</td>
        <td>{formatDate(user?.demoenddate || user?.demoEndDate)}</td>
        <td>{formatDate(user?.createdAt)}</td>
      </tr>
      );
    });
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">{t("admin_users_pages.list_title")}</h4>
              <div className="mb-3 d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className={`btn ${packageFilter === "all" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => {
                    setPackageFilter("all");
                    setPage(1);
                  }}
                >
                  {t("admin_common.all")}
                </button>
                <button
                  type="button"
                  className={`btn ${packageFilter === "premium" ? "btn-warning" : "btn-outline-warning"}`}
                  onClick={() => {
                    setPackageFilter("premium");
                    setPage(1);
                  }}
                >
                  {t("admin_common.premium")}
                </button>
                <button
                  type="button"
                  className={`btn ${packageFilter === "standard" ? "btn-secondary" : "btn-outline-secondary"}`}
                  onClick={() => {
                    setPackageFilter("standard");
                    setPage(1);
                  }}
                >
                  {t("admin_common.standard")}
                </button>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("admin_common.search_name_placeholder")}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>{t("admin_common.user_id")}</th>
                      <th>{t("admin_common.name")}</th>
                      <th>{t("admin_common.email")}</th>
                      <th>{t("admin_common.role")}</th>
                      <th>{t("admin_common.status")}</th>
                      <th>{t("admin_common.package")}</th>
                      <th>{t("admin_common.pricing_date")}</th>
                      <th>{t("admin_common.demo_start")}</th>
                      <th>{t("admin_common.demo_end")}</th>
                      <th>{t("admin_common.created_at")}</th>
                    </tr>
                  </thead>
                  <tbody>{renderRows()}</tbody>
                </table>
              </div>
              <div className="d-flex align-items-center justify-content-between mt-3">
                <span className="text-muted">
                  {t("admin_common.total_records", { count: filteredUsers.length })}
                </span>
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    {t("admin_common.prev")}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" disabled>
                    {currentPage} / {totalPages}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    {t("admin_common.next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UsersListPage;
